import get from 'lodash/get.js'

import {getState, useSelector} from '../../../store.js'
import {
  labelConfigSelector,
  attachCustomsInfoSelector,
  taxInfoErrorMessageSelector,
  ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES,
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
  SENDER_TAX_INFO,
  RECEIVER_TAX_INFO,
  taxIDNumberTemplate,
  taxIDTypeTemplate,
  taxIssuerCountryTemplate,
  VALID_TAX_INFO_SHIPPER_TYPES,
  taxInfoProperties,
  TAX_INFO_NEEDS_COUNTRY_SHIPPERS,
} from '../../../data/labelInfos/index.js'
import {showTaxInfoModal} from '../Modals/TaxInfoModal.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import ShipperOptions from '../../../data/shipperOptions.js'
import ConfigButton from './ConfigButton.js'

const VALID_LABEL_PROPERTIES = [SENDER_TAX_INFO, RECEIVER_TAX_INFO].reduce(
  (prev, property) => [
    ...prev,
    taxIDNumberTemplate(property),
    ...VALID_TAX_INFO_SHIPPER_TYPES.map((shipperType) =>
      taxIDTypeTemplate(shipperType, property),
    ),
    taxIssuerCountryTemplate(property),
  ],
  [],
)

const TAX_INFO_DEPENDENT_PROPERTIES = [
  ...ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES,
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
]

function dependentPropertiesFunc() {
  return TAX_INFO_DEPENDENT_PROPERTIES
}

function labelPropertiesFunc(shipperType, labelInfoID) {
  const attachCustomsInfo = attachCustomsInfoSelector(getState(), {labelInfoID})
  const properties = taxInfoProperties(shipperType)
  const labelProperties = []

  if (
    !attachCustomsInfo ||
    !VALID_TAX_INFO_SHIPPER_TYPES.includes(shipperType)
  ) {
    return labelProperties
  }

  for (const property of properties) {
    labelProperties.push(
      taxIDNumberTemplate(property),
      taxIDTypeTemplate(shipperType, property),
      taxIssuerCountryTemplate(property),
    )
  }

  return labelProperties
}

export default function TaxInfo() {
  const {labelInfoID, shipperType} = useLabelConfigContext()
  const propertySpecs = TaxInfo.propertySpecList[0]
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  const labelConfig = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const attachCustomsInfo = useSelector((state) =>
    attachCustomsInfoSelector(state, {labelInfoID}),
  )
  const taxInfoErrorMessage = useSelector((state) =>
    taxInfoErrorMessageSelector(state, {labelInfoID, shipperType}),
  )

  const properties = taxInfoProperties(shipperType)
  const options = get(ShipperOptions, [shipperType, 'tax_id_type'], [])

  const descriptions = []

  for (const property of properties) {
    const taxIDLabelProperty = taxIDNumberTemplate(property)

    const taxID = labelConfig[taxIDLabelProperty] || ''

    if (!taxID) {
      continue
    }

    const taxIDType =
      labelConfig[taxIDTypeTemplate(shipperType, property)] || ''
    const taxIDTypeOption = options.find(({value}) => value === taxIDType)
    const taxIssuerCountry =
      labelConfig[taxIssuerCountryTemplate(property)] || ''
    const hasCountry =
      taxIssuerCountry && TAX_INFO_NEEDS_COUNTRY_SHIPPERS.includes(shipperType)

    descriptions.push(
      `${property === SENDER_TAX_INFO ? 'Sender' : 'Receiver'} ID: ${taxID}${
        taxIDTypeOption ? ` – ${taxIDTypeOption.display}` : ''
      }${hasCountry ? ` – ${taxIssuerCountry}` : ''}`,
    )
  }

  return (
    <ConfigButton
      label="Tax Info"
      className="wrap--undo-ib"
      onClick={showTaxInfoModal}
      labelProperty={labelProperty}
      propertySpecs={propertySpecs}
      errorMessage={taxInfoErrorMessage}
      hide={!attachCustomsInfo}
    >
      {descriptions.length ? (
        <strong className="fs-n0 lh-md margin-bottom-1 block">
          {descriptions.join(', ')}
        </strong>
      ) : null}
      <span className="block">Edit</span>
    </ConfigButton>
  )
}

TaxInfo.propertySpecList = [
  {
    validShipperTypes: VALID_TAX_INFO_SHIPPER_TYPES,
    labelPropertiesFunc,
    dependentPropertiesFunc,
    validLabelPropertiesFunc: () => VALID_LABEL_PROPERTIES,
  },
]
